// SEO-EXPANSION v4: additive, no behavior change to existing pages.
import { createFileRoute, notFound } from "@tanstack/react-router";
import { findIntent } from "@/lib/seo/intent-registry";
import { brandTail } from "@/lib/seo/title-tails";
import { LiveRatesTable } from "@/components/seo/LiveRatesTable";
import { MetalConverter } from "@/components/seo/MetalConverter";
import { IntentCopy } from "@/components/seo/IntentCopy";
import { RelatedRatesMesh } from "@/components/seo/RelatedRatesMesh";
import { AuthorByline } from "@/components/seo/AuthorByline";
import { buildIntentJsonLd } from "@/components/seo/IntentSchema";

const BASE = "https://drhint.com";

export const Route = createFileRoute("/rates/$intentSlug")({
  loader: ({ params }) => {
    const node = findIntent(params.intentSlug);
    if (!node) throw notFound();
    return { node, year: new Date().getFullYear() };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return {};
    const { node, year } = loaderData;
    const tail = brandTail(node.slug);
    const title = `${node.titlePattern
      .replace("{exactKeyword}", node.exactKeyword)
      .replace("{YEAR}", String(year))} ${tail}`;
    const desc = `Live ${node.exactKeyword} updated every minute. Verified ${year} rates, ${node.unit} & ${node.karat ?? "all karats"} conversions, historical context. DrHint — trusted by ${node.region.toUpperCase()} users.`;
    const url = `${BASE}/rates/${node.canonicalSibling ?? node.slug}`;
    const selfUrl = `${BASE}/rates/${params.intentSlug}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { name: "robots", content: "index,follow,max-image-preview:large" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: selfUrl },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: buildIntentJsonLd(node, selfUrl).map((ld) => ({
        type: "application/ld+json",
        children: JSON.stringify(ld),
      })),
    };
  },
  component: IntentPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-2xl font-bold">Rate page not found</h1>
      <p className="mt-2 text-muted-foreground">This long-tail intent is not yet published.</p>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-2xl font-bold">Something went wrong</h1>
      <p className="mt-2 text-muted-foreground">{(error as Error).message}</p>
    </div>
  ),
});

function IntentPage() {
  const { node } = Route.useLoaderData();
  return (
    <main className="mx-auto max-w-5xl px-4 py-8">
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-primary">
          {node.metal} · {node.region.toUpperCase()} · {node.intentCluster}
        </p>
        <h1 className="mt-2 text-2xl md:text-3xl font-black leading-tight">
          {node.h1Pattern}
        </h1>
        <AuthorByline />
      </header>
      <LiveRatesTable node={node} />
      <MetalConverter node={node} />
      <IntentCopy node={node} />
      <RelatedRatesMesh node={node} />
    </main>
  );
}